import React, { useMemo, useEffect } from "react";
import { Chip, Avatar, Stack, Typography, Box } from "@mui/material";
import { MimshakPensionService } from "../../services/mimshakPensionService";
import { formatRelationType } from "../../utils/pensionEnumFormatter";
import { formatAge } from "../../utils/namesFormater";
import { RelationType } from "../../interfaces/DTO/mislaka_xml_pension_models";
import { Product } from "../../interfaces/DTO/mislaka_xml_models";
import { colorRelationType } from "../../utils/colorEnumFormatter";
import { useContacts } from "../../hooks/crm/useContacts";
import PersonIcon from "@mui/icons-material/Person";
import { useRelatedContactsStore } from "../../stores/selectedToViewStore";
import { Contact } from "../../interfaces/DTO/crm/contact";

interface BeneficiaryChipsProps {
  products: Product[];
  handleFamilyMemberClick?: (userId: number) => void;

}


const BeneficiaryChips: React.FC<BeneficiaryChipsProps> = ({ products, handleFamilyMemberClick }) => {
  const beneficiaries = useMemo(
    () => MimshakPensionService.getBeneficiaryInfoDistinct(products),
    [products]
  );
  const { data: contacts = [] } = useContacts();
  // only the setter: subscribing to the whole store re-renders on every set → effect → set ... (infinite loop)
  const setRelatedContacts = useRelatedContactsStore((s) => s.setRelatedContacts);

  // מציאת ה-contacts המלאים של המוטבים שהם גם לקוחות
  const beneficiaryContacts = useMemo(() => {
    const foundContacts: Contact[] = [];
    if (beneficiaries.length === 0 || contacts.length === 0) return foundContacts;

    for (const beneficiary of beneficiaries) {
      if (!beneficiary.id_number) continue;

      const matchingContact = contacts.find(c => c.israeli_id === beneficiary.id_number);
      if (matchingContact) {
        foundContacts.push(matchingContact);
      }
    }
    return foundContacts;
  }, [contacts, beneficiaries]);

  // עדכון ה-store עם ה-relatedContacts
  useEffect(() => {
    setRelatedContacts(beneficiaryContacts);
  }, [beneficiaryContacts, setRelatedContacts]);
  
return (
    <Box
      sx={{
        overflowX: "auto",
        whiteSpace: "nowrap",
        width: "100%",
        paddingTop: 1, // מקום ל־relationText
      
      }}
    >
      <Stack direction="row" spacing={1} sx={{   mr: 1, }}>
        {beneficiaries.map((beneficiary, index) => {
          const firstName = beneficiary.first_name?.trim() || "";
          const lastName = beneficiary.last_name?.trim() || "";
          const namesEqual =
            firstName.toLowerCase() === lastName.toLowerCase() && firstName !== "";
          const fullName = namesEqual
            ? firstName
            : `${firstName} ${lastName}`.trim();

          const avatarLetter = firstName.charAt(0).toUpperCase() || "?";
          const chipColor = colorRelationType(
            beneficiary.relation_type as RelationType
          );
          const relationText = formatRelationType(
            beneficiary.relation_type as RelationType
          );

          const ageText = beneficiary.birth_date
            ? formatAge(beneficiary.birth_date)
            : "לא ידוע";

                 // למצוא אם יש לקוח עם אותו ת"ז
          const matchingContact = beneficiary.id_number
            ? contacts.find(c => c.israeli_id === beneficiary.id_number)
            : undefined;
          const isClient = Boolean(matchingContact);

          return (
            <Box
              position="relative"
              key={index}
              display="inline-block"
              sx={{ flex: "0 0 auto" }}
              
            >
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  top: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  bgcolor: "white",
                  lineHeight: 1,
                  px: 0.5,
                  fontSize: "12px",
                  fontWeight: "bold",
                  color: chipColor,
                  whiteSpace: "nowrap",
                  borderRadius: 5,
                  zIndex: 1,
                  pl: 1,
                  pr: 1,
              
                }}
              >
                {relationText}
              </Typography>

              <Chip
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: chipColor,
                      color: "white",
                      width: 50,
                      height: 50,
                      fontSize: 20,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: "bold", fontSize: "22px" }}
                    >
                      {avatarLetter}
                    </Typography>
                  </Avatar>
                }
                label={
                  <>
                    {isClient && (
                      <PersonIcon
                        fontSize="small"
                        sx={{ verticalAlign: "middle", mr: 0 }}
                      />
                    )}
                    {`${fullName}: ${ageText}`}
                  </>
                }
                variant="outlined"
                sx={{
                  bgcolor: chipColor + "20",
                  borderColor: chipColor,
                  color: chipColor,
                  fontWeight: 600,
                  cursor: isClient ? "pointer" : "default",
                  ".MuiChip-label": {
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: 1.5,
                    paddingRight:0.5
                  },
                }}
                title={
                  beneficiary.birth_date
                    ? `תאריך לידה: ${beneficiary.birth_date}`
                    : ""
                }
                onClick={() => {
                  if (isClient && matchingContact && handleFamilyMemberClick) {
                    handleFamilyMemberClick(matchingContact.id!);
                  }
                }}
              />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
export default BeneficiaryChips;